<template>
  <div id="billing-form">
    <div
      v-show="isProcessing"
      class="section section--loading center"
    >
      <div class="loader loader--large" />
      <p class="loader-wrapper__text">Processing...</p>
    </div>

    <div v-show="!isProcessing">
      <div class="section section--half-y-padding">
        <div class="layout layout--horizontal">
          <button
            class="link link--has-title"
            type="button"
            tabindex="-1"
            @click="$closeModal('paymentInformation')"
          >
            <div class="layout layout--icon layout--icon-extra-spacing">
              <span class="icon icon--smedium far fa-arrow-left dark-gray" />

              <h2 class="title title--2">
                <template v-if="total === 0 && currentCapiOrderType !== 'TipOnly'">
                  Place Order
                </template>
                <template v-else>
                  {{ paymentModalTitle ? paymentModalTitle : content.PaymentScreen.title }}
                </template>
              </h2>
            </div>
          </button>

          <button
            v-if="userAccountFeatureEnabled && !currentUser.accountId && !hiddenElements.signIn"
            class="button button--secondary button--small button--auto-width normalcase"
            type="button"
            @click="$openModal('signIn', {
              onSuccess: onSignInSuccess
            })"
          >
            {{ content.PaymentScreen.metadata.signInCta }}
          </button>
        </div>
      </div>

      <div
        v-show="showLoginReminder && !hiddenElements.signIn"
        class="section section--danger section--shadow section--half-y-padding reverse-colors"
      >
        <p>{{ content.PaymentScreen.metadata.checkoutSignInReminder }}</p>
      </div>

      <slot name="top" />

      <div
        v-if="loadingAltPaymentMethods"
        class="section"
      >
        <div class="loader" />
      </div>

      <template v-else-if="!hiddenElements.altPaymentMethods && (total > 0 && totalRemainingBalance > 0) || currentCapiOrderType === 'TipOnly'">
        <alternative-payment-methods
          ref="selectableMethods"
          :hidden-elements="hiddenElements"
          :payment-gateway="paymentGateway"
          :force-save-cards="forceSaveCards"
          :gift-card-reload-payment-method="giftCardReloadPaymentMethod"
          :selected-payment-method="selectedPayment.method"
          :total="totalRemainingBalance"
          :show-gift-card-button="showGiftCardButton"
          :show-room-charge-button="showRoomChargeButton"
          @method-select="handlePaymentMethodSelect"
          @unsupported-method="methodName => unsupportedPaymentMethods.push(methodName)"
          @error="paymentMethodErrorHandler"
          @before-submit="validatePaymentMethod"
          @submitted="paymentMethodSubmittedHandler"
        />

        <Teleport to="#payment-method-charge-room">
          <button
            v-if="showRoomChargeButton"
            class="button button--large button--black"
            type="button"
            @click="$openModal('chargeToRoom', { hideSms: currentCapiOrderType === 'TipOnly' })"
          >
            Charge Room
          </button>
        </Teleport>

        <!-- NOTE: The extra pay/table v-if check is to temporarily disable gift cards for P@T/O@T -->
        <Teleport to="#payment-method-gift-card">
          <button
            v-if="showGiftCardButton"
            class="button button--large button--black"
            type="button"
            @click="$openModal('addGiftCard')"
          >
            Gift Card
          </button>
        </Teleport>
      </template>

      <div
        v-if="totalRemainingBalance === 0 && totalGiftCardBalanceApplied > 0"
        class="section section--half-y-padding section--success reverse-colors animation-fade-in"
      >
        <p>
          Your gift {{ giftCards.appliedCards.length === 1 ? 'card currently covers' : 'cards currently cover' }}
          the full balance of your order. Deselect to use a different payment method.
        </p>
      </div>

      <validated-form
        v-if="displaySubmitForm"
        ref="paymentForm"
        name="cardInformation"
        @valid-submit="submitPaymentHandler"
      >
        <div
          v-if="paymentGateway.methods.creditCard && (totalRemainingBalance > 0 || currentCapiOrderType === 'TipOnly')"
          class="section section--half-y-padding"
        >
          <!-- Saved Credit Cards -->
          <div v-if="saveCreditCardFeatureEnabled && !noPreviouslySavedCards">
            <div class="layout layout--horizontal">
              <h3 class="title title--6">
                {{ content.PaymentScreen.metadata.savedCards }}
              </h3>

              <!-- Add New Credit Card Button -->
              <button
                v-if="!displayCardInformationForm"
                class="button button--small button--icon button--secondary"
                type="button"
                title="Add New Card"
                @click="addNewPaymentHandler"
              >
                <span class="icon far fa-plus" />
              </button>
            </div>

            <!-- Saved Cards List -->
            <saved-cards
              v-model="selectedPayment.cardId"
              :cvv-input="cvv"
              @saved-card-focus="revealContactInfo"
              @cvv-input-change="cvvInputChangeHandler"
            />
          </div>

          <transition name="fade-down-only-in">
            <div v-if="displayCardInformationForm">
              <hr v-if="saveCreditCardFeatureEnabled && !noPreviouslySavedCards">

              <h3 class="title title--6">
                {{ content.PaymentScreen.metadata.cardInfo }}
              </h3>

              <!-- Card Information Input Form -->
              <card-information
                ref="cardInformation"
                :payment-gateway="paymentGateway"
                :tds-provider="tdsProvider.threeDsProvider"
                :include-shipping-address-for-all-cards="!!tdsProvider.includeShippingAddressForAllCards"
                :require-billing-address="requireBillingAddress"
                :skip-required-validation="selectedPayment.method !== 'CreditCard'"
                @success="cardInformationSuccessHandler"
                @interacted="cardInformationInteractionHandler"
                @error="cardInformationErrorHandler"
              />

              <!-- Save Payment For Future Order Checkbox -->
              <checkbox-input
                v-if="saveCreditCardFeatureEnabled"
                v-model="saveNewPaymentMethod"
                name="shouldSaveCard"
                :disabled="forceSaveCards"
              >
                Save card for future orders
              </checkbox-input>

              <h5
                v-if="saveCreditCardFeatureEnabled && content.PaymentScreen.metadata.addCreditCardTerms"
                class="cf-title cf-title--5 cf-text-align-left"
                v-html="content.PaymentScreen.metadata.addCreditCardTerms"
              />
            </div>
          </transition>

          <div
            v-if="!!creditCardSurcharge"
            class="flex spacing half-gap align-center"
          >
            <p class="smaller no-spacing">
              A <span class="primary"> {{ creditCardSurcharge.surchargeAmount }}% surcharge </span>will be added for credit cards.
            </p>

            <button
              type="button"
              class="cursor-pointer auto-spacing-right"
              @click="$openModal('notice', { title: creditCardSurcharge.surchargeModalTitle , description: creditCardSurcharge.surchargeModalDescription , useSecondaryStyle: true })"
            >
              <span class="icon fal fa-info-circle gray" />
            </button>
          </div>
        </div>

        <!-- Gift Cards List -->
        <gift-cards
          v-if="!hiddenElements.giftCards"
          :payment-gateway="paymentGateway"
          :can-apply-gift-cards="!hasReachedAppliedCardLimit && totalRemainingBalance > 0"
          :gift-cards="giftCards.cards"
        >
          <template #below-listed-cards>
            <transition-group name="fade">
              <p
                v-if="totalRemainingBalance > 0 && hasReachedAppliedCardLimit"
                key="card-limit-message"
                class="message message--small"
              >
                You've applied the limit of {{ merchant.features['gift-card-limit-on-order'] }}
                gift card{{ Number(merchant.features['gift-card-limit-on-order']) > 1 ? 's' : '' }} to this order
              </p>

              <p
                v-if="totalRemainingBalance > 0 && totalGiftCardBalanceApplied > 0"
                key="select-additional-payment-message"
                class="message message--success"
              >
                Please select an additional payment method above to cover the remaining balance: {{ dollars(totalRemainingBalance) }}
              </p>
            </transition-group>
          </template>
        </gift-cards>

        <!-- Contact Information -->
        <template v-if="isContactInfoVisible">
          <div class="section section--half-y-padding">
            <template v-if="currentUser.accountId && hiddenElements.contactInfo === 'read-only'">
              <h3 class="title title--6">
                Contact Info
              </h3>

              <div class="layout layout--icon">
                <span class="icon icon--smedium fal fa-envelope dark-gray" />
                <p>
                  You’ll recieve a confirmation email at:
                  <br>
                  <span class="dark">{{ currentUser.email }}</span>
                </p>
              </div>
            </template>
            <contact-information
              v-else
              v-model="contactInfo"
              class="animation-fade-in"
              :email-input-state="emailInputState"
              :phone-is-required="phoneIsRequired"
              :tds-provider="tdsProvider.threeDsProvider"
              :request-billing-email="requestBillingEmail"
              :request-billing-phone="requestBillingPhone"
            />
          </div>

          <!-- Text Receipt Opt In-->
          <sms-receipt-opt-in
            v-if="!hiddenElements.smsReceipt"
            class="section section--half-y-padding"
          />
        </template>

        <!-- Footer -->
        <div
          ref="footer"
          class="section section--half-y-padding section--alt-background section--shadow"
        >
          <transition name="fade-up-only-in">
            <div v-if="showTotalBreakdown">
              <div class="right spacing">
                <button
                  class="button button--inline button--secondary button--icon button--small"
                  type="button"
                  @click="toggleTotalBreakDownHandler"
                >
                  <span class="icon far fa-times" />
                </button>
              </div>

              <div class="key-value key-value--small">
                <dl class="key-value__row">
                  <dt class="key-value__key">
                    Your Total
                  </dt>
                  <dd class="key-value__value">
                    {{ dollars(total) }}
                  </dd>
                </dl>

                <dl class="key-value__row">
                  <dt class="key-value__key">
                    Applied Gift Card Balance
                  </dt>
                  <dd class="key-value__value">
                    -{{ dollars(totalGiftCardBalanceApplied) }}
                  </dd>
                </dl>

                <dl class="key-value__row">
                  <dt class="key-value__key bold">
                    Remaining Balance Due
                  </dt>
                  <dd class="key-value__value bold">
                    {{ dollars(totalRemainingBalance) }}
                  </dd>
                </dl>
              </div>

              <hr>
            </div>
          </transition>

          <div :class="['grid three-quarter-gap', hiddenElements.total ? 'col-1' : 'col-2']">
            <div v-if="!hiddenElements.total">
              <p class="smaller dark-gray no-spacing">
                <template v-if="totalRemainingBalance !== total">
                  Balance Due
                </template>

                <template v-else>
                  {{ paymentTotalTitle }}
                </template>
              </p>

              <div class="layout layout--horizontal layout--horizontal-less-space layout--justify-start">
                <p class="title title--3 bold no-wrap">
                  <b>{{ dollars(totalRemainingBalance) }}</b>
                </p>

                <button
                  v-if="totalRemainingBalance !== total"
                  v-show="!showTotalBreakdown"
                  class="icon icon--link inline-block spacing dark-gray"
                  type="button"
                  @click="toggleTotalBreakDownHandler"
                >
                  <span class="icon icon--smedium fal fa-info-circle" />
                </button>
              </div>
            </div>

            <button
              class="button button--primary button--large button--auto-width"
              type="submit"
              :disabled="isSubmitPaymentDisabled"
            >
              {{ paymentFormSubmitButtonText }}
            </button>
          </div>
        </div>
      </validated-form>
    </div>


    <modal
      v-slot="{ data }"
      name="missingContactInformation"
      modal-style="compact"
      :is-required="true"
    >
      <validated-form
        name="missingContactInformation"
        @valid-submit="submitMissingContactInfoHandler(data.options)"
      >
        <div class="section section--half-padding center">
          <h2 class="title title--2 half-spacing">
            Almost done!
          </h2>

          <p v-if="data.emailIsInvalid && data.phoneIsInvalid">We just need some extra contact info before we can submit your order:</p>
          <p v-else-if="data.phoneIsInvalid">We just need your phone number in case we have to contact you about your order:</p>
          <p v-else-if="data.emailIsInvalid">We just need your email so we can send you a receipt:</p>

          <text-input
            v-if="data.emailIsInvalid"
            v-model="email"
            type="email"
            label="Email"
            name="emailMissingCheckout"
            :validations="{
              required: true
            }"
          />

          <text-input
            v-if="data.phoneIsInvalid"
            v-model="phoneNumber"
            type="phone"
            label="Phone Number"
            name="phoneMissingCheckout"
            autocomplete="tel"
            :validations="{
              required: true
            }"
          />

          <button
            class="button button--primary button--large"
            type="submit"
          >
            Submit
          </button>

        </div>
      </validated-form>
    </modal>
  </div>
</template>

<script>
  import AlternativePaymentMethods from './alternative-payment-methods.vue';
  import CardInformation from './card-information.vue';
  import ContactInformation from './contact-information.vue';
  import GiftCards from './gift-cards.vue';
  import errorHandlers from '@/services/error-handlers';
  import { mapGetters } from 'vuex';
  import dollars from '@/filters/dollars';

  export default {
    name: 'PaymentInformation',

    components: {
      CardInformation,
      ContactInformation,
      GiftCards,
      AlternativePaymentMethods
    },

    props: {
      emailInputState: {
        type: String,
        default: 'required',
        validator(value) {
          return ['required', 'optional', 'hidden'].includes(value);
        }
      },

      forceSaveCards: {
        type: Boolean,
        default: false
      },

      hiddenElements: {
        type: Object,
        default: () => ({})
      },

      isPreAuth: {
        type: Boolean,
        default: false
      },

      loadingAltPaymentMethods: {
        type: Boolean,
        default: false
      },

      payCtaText: {
        type: String,
        default: 'Pay'
      },

      paymentGateway: {
        type: Object,
        required: true
      },

      paymentModalTitle: {
        type: String,
        default: null
      },

      paymentTotalTitle: {
        type: String,
        default: 'Your Total'
      },

      phoneIsRequired: {
        type: Boolean,
        default: false
      },

      giftCardReloadPaymentMethod: {
        type: Object,
        default: null
      },

      roomNumber: {
        type: String,
        default: null
      },

      tip: {
        type: Number,
        default: 0
      },

      total: {
        type: Number,
        required: true
      },

      tdsProvider: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        contactInfo: {},
        email: null,
        isProcessing: false,
        phoneNumber: null,
        showContactInfo: false,
        showTotalBreakdown: false,
        saveNewPaymentMethod: true,
        /**
         * selectedPayment.method === 'CreditCard' || 'GooglePay' || 'ApplePay'
        */
        selectedPayment: {
          method: 'CreditCard',
          cardId: null
        },
        cvv: null,
        tdsService: {},
        cardInfo: null,
        unsupportedPaymentMethods: []
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('giftCards', ['giftCards']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('offers', ['offers']),
      ...mapGetters('orders', ['order', 'currentCapiOrderType']),
      ...mapGetters('savedCards', ['savedCards']),
      ...mapGetters('session', ['currentUser', 'orderDetails', 'session']),

      requireBillingAddress() {
        const selectedLocationCreditCardMethod = this.selectedLocation.paymentGateway.methods.creditCard;

        return selectedLocationCreditCardMethod ? selectedLocationCreditCardMethod.requireFullBillingAddress
          : this.merchant.defaultPaymentGateway.methods.creditCard.requireFullBillingAddress;
      },

      requestBillingEmail() {
        const selectedLocationCreditCardMethod = this.selectedLocation.paymentGateway.methods.creditCard;

        return selectedLocationCreditCardMethod ? !!selectedLocationCreditCardMethod.requestBillingEmail
          : !!this.merchant.defaultPaymentGateway.methods.creditCard.requestBillingEmail;
      },

      requestBillingPhone() {
        const selectedLocationCreditCardMethod = this.selectedLocation.paymentGateway.methods.creditCard;

        return selectedLocationCreditCardMethod ? !!selectedLocationCreditCardMethod.requestBillingPhone
          : !!this.merchant.defaultPaymentGateway.methods.creditCard.requestBillingPhone;
      },

      noPreviouslySavedCards() {
        return this.savedCards.hasLoaded && !this.savedCards.cards.length;
      },

      userAccountFeatureEnabled() {
        return this.merchant.options.signUpEnabled;
      },

      saveCreditCardFeatureEnabled() {
        return [
          this.userAccountFeatureEnabled,
          this.session.isSignedIn,
          !this.merchant.features['disable-saved-payment-management'],
          !!this.paymentGateway.methods.savedCreditCard,
          !this.merchant.features['disable-ordering']
        ].every(Boolean);
      },

      displayCardInformationForm() {
        const noSavedCardSelected = !this.selectedPayment.cardId;

        if (this.paymentGateway.methods.creditCard.active) {
          if (this.selectedPayment.method === 'SavedCard') {
            return false;
          }

          if (this.selectedPayment.method !== 'CreditCard' && noSavedCardSelected && !this.savedCards.cards.length) {
            return true;
          }

          return this.selectedPayment.method === 'CreditCard';
        }

        return false;
      },

      isContactInfoVisible() {
        return this.hiddenElements.contactInfo !== true && (
          (this.totalRemainingBalance === 0 && this.currentCapiOrderType !== 'TipOnly')
          || this.hiddenElements.contactInfo === 'read-only'
          || this.showContactInfo
        );
      },

      appliedGiftCards() {
        return this.totalGiftCardBalanceApplied > 0 ? this.giftCards.appliedCards : [];
      },

      displaySubmitForm() {
        return !!this.paymentGateway.methods.creditCard || !!this.paymentGateway.methods.giftCard || this.total === 0;
      },

      showLoginReminder() {
        return !this.session.isSignedIn && !!this.content.PaymentScreen.metadata.checkoutSignInReminder;
      },

      paymentFormSubmitButtonText() {
        return this.total === 0 && this.currentCapiOrderType !== 'TipOnly'
          ? 'Submit'
          : this.payCtaText;
      },

      isSubmitPaymentDisabled() {
        return this.unsupportedPaymentMethods.includes(this.selectedPayment.method)
          || (this.totalRemainingBalance > 0 && !this.paymentGateway.methods.creditCard);
      },

      totalGiftCardBalanceApplied() {
        if (this.hiddenElements.giftCards) {
          return 0;
        }

        return Math.min(this.total, this.giftCards.totalBalanceApplied);
      },

      totalRemainingBalance() {
        const total = Math.round((this.total - this.totalGiftCardBalanceApplied) * 100) / 100;

        return Math.max(0, total);
      },

      hasReachedAppliedCardLimit() {
        const giftCardLimit = Number(this.merchant.features['gift-card-limit-on-order']);
        const isUnlimited = giftCardLimit <= 0;

        return isUnlimited ? false : giftCardLimit <= this.giftCards.appliedCards.length;
      },

      areGiftCardsLoading() {
        return this.$store.state.giftCards.areLoading;
      },

      showGiftCardButton() {
        return this.paymentGateway.methods.giftCard
          && !this.hiddenElements.giftCards
          && !this.areGiftCardsLoading
          && this.giftCards.cards.length === 0
          && !this.isPreAuth;
      },

      showRoomChargeButton() {
        return this.paymentGateway.methods.roomCharge && !this.hiddenElements.roomCharge;
      },

      creditCardSurcharge() {
        if (this.paymentGateway.methods.creditCard?.surcharge) {
          const surchargeAmount = this.paymentGateway.methods.creditCard.surcharge.percentAmount;

          return {
            surchargeAmount,
            surchargeModalTitle: 'Credit Card Surcharge',
            surchargeModalDescription: `We impose a surcharge of ${surchargeAmount}% on the transaction amount on credit cards, which is not greater than our cost of acceptance. <b>We do not surcharge debit cards.<b>`
          };
        }

        return null;
      }
    },

    watch: {
      isProcessing: 'isProcessingChangeHandler',

      'savedCards.cards': 'savedCardsChangeHandler',

      'session.isSignedIn': {
        handler(isSignedIn) {
          if (isSignedIn) this.fetchSavedCards();
        }
      },

      'selectedPayment.cardId': {
        handler(newCardId) {
          if (newCardId) this.handlePaymentMethodSelect('SavedCard', newCardId);
        }
      }
    },

    async created() {
      await this.fetchSavedCards();

      this.triggerGoogleEventTracking();
    },

    methods: {
      dollars,

      async onSignInSuccess() {
        /* check to see if the newly signed in user has redeemable offers available */
        if (this.offers.allRedeemableOffers.length) {
          /**
           * if they do, refetch the order now that there is a signed-in user
           * so that the `order.orderAttributes` can be refreshed to accurately
           * reflect the ability to allow adding account offers.
           */
          await this.$store.dispatch('orders/getOrder', { silent: true });
          /**
           * close the payment modal and place the user back on the
           * order summary to view their redeemable offers
           */
          this.$closeModal('paymentInformation');
        }
      },

      async fetchSavedCards() {
        if (this.saveCreditCardFeatureEnabled) await this.$store.dispatch('savedCards/getCards');
      },

      async initializeTdsService() {
        const tdsServiceConfig = {
          order: this.order,
          tdsProviderDetails: this.tdsProvider,
          merchantUuid: this.tdsProvider.merchantUuid
        };

        this.tdsService = await this.tdsProvider.tdsProviderService(tdsServiceConfig);
      },

      savedCardsChangeHandler() {
        /* only handle pre-selecting a payment method once saved cards have been loaded */
        if (this.savedCards.hasLoaded) {
          const defaultCard = this.savedCards.validCards.find(card => card.isUsersDefaultCard);
          const [firstCard] = this.savedCards.validCards;
          const defaultSelectedCardId = defaultCard?.creditCardId || firstCard?.creditCardId || null;

          if (this.giftCardReloadPaymentMethod) {
            /* when setting up auto-reload on a card for the first time */
            if (!this.giftCardReloadPaymentMethod.method) {
              this.handlePaymentMethodSelect('CreditCard', defaultSelectedCardId);
            }
            /* when auto-reload is currently configured with a saved credit card */
            else if (this.giftCardReloadPaymentMethod.creditCardId && this.giftCardReloadPaymentMethod.method === 'SavedCreditCard') {
              this.handlePaymentMethodSelect('CreditCard', this.giftCardReloadPaymentMethod.creditCardId);
            }
            /* when auto-reload is currently configured with an alt payment method */
            else {
              this.handlePaymentMethodSelect(this.giftCardReloadPaymentMethod.method);
            }
          }
          else {
            const methodType = defaultSelectedCardId ? 'SavedCard' : 'CreditCard';
            this.handlePaymentMethodSelect(methodType, defaultSelectedCardId);
          }

          if (this.selectedPayment.cardId) this.revealContactInfo();
        }
      },

      handlePaymentMethodSelect(paymentMethod, cardId = null) {
        this.selectedPayment = { method: paymentMethod, cardId };

        if (paymentMethod !== 'CreditCard' && this.$refs.paymentForm) this.$refs.paymentForm.resetValidation();
      },

      validatePaymentMethod({ method, onValidated }) {
        /** This allows to run validations upon clicking GP/AP if needed in the future */
        this.handlePaymentMethodSelect(method);
        onValidated(method);
      },

      paymentMethodErrorHandler(error) {
        this.isProcessing = false;
        if (error.statusCode !== 'CANCELED') {
          errorHandlers.generic(error, {
            fallbackMessage: 'Unable to process payment.'
          });
        }
      },

      cardInformationSuccessHandler(paymentInformation) {
        this.paymentMethodSubmittedHandler({
          ...paymentInformation,
          contactInfo: this.contactInfo
        });
      },

      cardInformationInteractionHandler() {
        this.handlePaymentMethodSelect('CreditCard');
        this.revealContactInfo();
      },

      revealContactInfo() {
        this.showContactInfo = true;
      },

      cardInformationErrorHandler(error) {
        this.isProcessing = false;

        errorHandlers.silent(error);
      },

      onCheckoutSuccess() {
        this.$closeModal('paymentInformation');
      },

      onCheckoutError() {
        this.isProcessing = false;
        this.$closeModal('paymentInformation');
      },

      addNewPaymentHandler() {
        this.saveNewPaymentMethod = true;
        this.cardInformationInteractionHandler();
      },

      cvvInputChangeHandler(cvvValue) {
        this.cvv = cvvValue;
      },

      isProcessingChangeHandler(isProcessing) {
        if (isProcessing) {
          this.$lockModal('paymentInformation');
        }

        else {
          this.$unlockModal('paymentInformation');
        }
      },

      async toggleTotalBreakDownHandler() {
        this.showTotalBreakdown = !this.showTotalBreakdown;

        if (this.showTotalBreakdown) {
          await this.$nextTick();

          this.$refs.footer.scrollIntoView({
            block: 'end'
          });
        }
      },

      normalizePaymentInfo(options) {
        const type = options.paymentMethod.body?.paymentType || options.paymentMethod.type;
        let { email, fullName, phoneNumber } = options.contactInfo;

        const shouldInheritAccountInfo = !!this.currentUser.accountId && /apple|google/gi.test(type);
        if (shouldInheritAccountInfo) {
          email = this.currentUser.email || email;
          fullName = this.currentUser.fullName || fullName;
          phoneNumber = this.currentUser.primarySmsNumber || phoneNumber;
        }

        return {
          ...options,
          contactInfo: {
            ...options.contactInfo,
            email,
            fullName,
            phoneNumber
          },
          paymentMethod: {
            ...options.paymentMethod,
            giftCards: this.appliedGiftCards
          },
          onCheckoutError: this.onCheckoutError,
          onCheckoutSuccess: this.onCheckoutSuccess
        };
      },

      validatePaymentContactInfo(options) {
        let isValid = true;
        const emailIsInvalid = (this.emailInputState === 'required' || this.requestBillingEmail)
          && !options.contactInfo.email && !this.hiddenElements.contactInfo;
        const phoneIsInvalid = (this.phoneIsRequired || this.session.smsReceiptOptIn || this.requestBillingPhone)
          && !options.contactInfo.phoneNumber && !this.hiddenElements.contactInfo;

        if (emailIsInvalid || phoneIsInvalid) {
          isValid = false;

          this.$openModal('missingContactInformation', {
            emailIsInvalid,
            options,
            phoneIsInvalid
          });
        }

        return isValid;
      },

      async saveContactInfoToAccount(info) {
        try {
          const { phoneNumber } = info.contactInfo;

          if (!this.currentUser.accountId || !phoneNumber || this.currentUser.primarySmsNumber) {
            return;
          }

          await this.$store.dispatch('session/updateProfile', {
            primarySmsNumber: phoneNumber
          });
        }

        catch (error) {
          errorHandlers.silent(error);
        }
      },

      async saveCardInfo(cardInfo) {
        /* attempt to save credit card */
        const savedCard = await this.$store.dispatch('savedCards/createCard', {
          cardInformation: cardInfo.paymentMethod,
          isSilentNotifiction: true
        });

        /* if save credit card succeeds, return saved-card payload */
        if (savedCard) {
          return {
            ...cardInfo,
            paymentMethod: {
              type: 'saved-card',
              savedCardId: savedCard.creditCardId,
              ...cardInfo.paymentMethod.cardDetails?.cvv && { cvv: cardInfo.paymentMethod.cardDetails.cvv },
              giftCards: cardInfo.paymentMethod.giftCards
            }
          };
        }
        /* else return original cardInfo payload */
        return cardInfo;
      },

      async handlePaymentTypes() {
        const isPayingWithNewCard = ['card', 'third-party-form'].includes(this.cardInfo.paymentMethod.type);
        const shouldSaveCard = isPayingWithNewCard && this.saveCreditCardFeatureEnabled && this.saveNewPaymentMethod;

        const cardInfo = shouldSaveCard
          ? await this.saveCardInfo(this.cardInfo)
          : this.cardInfo;

        this.$emit('checkout', cardInfo);
      },

      handleTdsResponse(response = null) {
        if (response) this.$store.commit('orders/SET_TDS_DATA', response);

        this.handlePaymentTypes();
      },

      handleTdsError() {
        this.$closeModal('paymentInformation');
        this.paymentMethodErrorHandler({});
      },

      async submitValidPayment() {
        const orderMeetsThreeDsThreshold = this.total >= this.tdsProvider.threeDsThreshold;
        /**
         * A merchant can opt a specific payment request out of 3DS when sending it
         * (e.g. they take the card over the phone). The opt-out arrives as a
         * `SkipThreeDsAuth` token in the order's `features` string; honor it as
         * threeDsRequired = false so the guest is never prompted for a challenge.
         */
        const skipThreeDsAuth = this.order.features?.includes('SkipThreeDsAuth');
        const runThreeDs = this.tdsProvider.threeDsRequired && !skipThreeDsAuth && orderMeetsThreeDsThreshold && this.cardInfo.paymentMethod.type === 'card';

        if (runThreeDs && this.tdsProvider.threeDsProvider === 'Clover') {
          /**
           * Clover runs 3DS as a post-payment action loop (see services/pay.js) not a pre-payment token.
           */
          await this.initializeTdsService();
          this.$store.commit('orders/SET_CLOVER_BROWSER_INFO', this.tdsService.getBrowserInfo());
          this.handlePaymentTypes();
        }
        else if (runThreeDs) {
          /**
           * The Paay SDK constructor scans the form DOM for `data-three-ds`
           * inputs and caches the input list — it does not re-scan later.
           * Construct it here, just before verify(), so the SDK sees the
           * current form state (including the AMEX-only shipping inputs,
           * which only render after the user has typed an AMEX card).
           */
          await this.initializeTdsService();
          this.tdsService.submitTdsTransaction(this.handleTdsResponse, this.handleTdsError, this.cardInfo);
        }
        else {
          this.handlePaymentTypes();
        }
      },

      paymentMethodSubmittedHandler(paymentInformation) {
        this.isProcessing = true;

        const cardInfo = this.normalizePaymentInfo(paymentInformation);
        const isValidContactInformation = this.validatePaymentContactInfo(cardInfo);

        if (isValidContactInformation) {
          this.isProcessing = true;
          this.cardInfo = cardInfo;

          this.saveContactInfoToAccount(cardInfo);

          this.submitValidPayment();
        }
      },

      submitPaymentHandler() {
        const paymentDetails = {
          contactInfo: this.contactInfo,
          paymentMethod: {
            type: null
          }
        };

        if (this.total === 0 && this.currentCapiOrderType !== 'TipOnly') {
          /**
          * This occurs when the total price of items in the cart is $0
          * or when a discount/reward was applied (and no tip) that results in the user spending $0
          */

          if (this.order.discount) {
            paymentDetails.paymentMethod.type = 'offer';
          }
          else if (this.selectedLocation.allowNoPaymentInfo) {
            paymentDetails.paymentMethod.type = 'none';
          }

          if (paymentDetails.paymentMethod.type) this.paymentMethodSubmittedHandler(paymentDetails);
        }

        else if (this.totalGiftCardBalanceApplied > 0 && this.totalRemainingBalance === 0) {
          paymentDetails.paymentMethod.type = 'gift-card';

          this.paymentMethodSubmittedHandler(paymentDetails);
        }

        else if (this.selectedPayment.cardId) {
          Object.assign(
            paymentDetails,
            {
              paymentMethod: {
                type: 'saved-card',
                savedCardId: this.selectedPayment.cardId,
                ...this.cvv && { cvv: this.cvv }
              }
            }
          );

          this.paymentMethodSubmittedHandler(paymentDetails);
        }

        else if (this.selectedPayment.method === 'GooglePay') {
          this.$refs.selectableMethods.$refs.alternativeMethods.submitMethod(this.selectedPayment.method);
        }

        else if (this.selectedPayment.method === 'ApplePay') {
          this.$refs.selectableMethods.$refs.alternativeMethods.submitMethod(this.selectedPayment.method);
        }

        else {
          this.isProcessing = true;
          this.$refs.cardInformation.submit();
        }
      },

      async submitMissingContactInfoHandler(dataOptions) {
        const options = JSON.parse(JSON.stringify(dataOptions));

        options.contactInfo.email = options.contactInfo.email || this.email;
        options.contactInfo.phoneNumber = options.contactInfo.phoneNumber || this.phoneNumber;

        if (this.currentUser.accountId && this.phoneNumber) {
          try {
            await this.$store.dispatch('session/updateProfile', {
              primarySmsNumber: this.phoneNumber
            });
          }

          catch (error) {
            errorHandlers.silent(error);
          }
        }

        this.$closeModal('missingContactInformation');
        this.paymentMethodSubmittedHandler(options);
      },

      triggerGoogleEventTracking() {
        const { orderItems, tax, orderItemTotal, offers } = this.order;

        const [firstOffer] = offers;

        const eventData = {
          itemInfo: orderItems,
          paymentInfo: {
            total: orderItemTotal,
            tax,
            tip: this.tip,
            ...firstOffer && { promoCode: firstOffer.cardfreeOfferPublicId },
            ...this.order.discount && { discount: this.order.discount }

          }
        };

        this.$googleEventTracking.postEvent('begin_checkout', eventData);
      }
    }
  };
</script>
